import { useCallback, useEffect, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fullAddress } from "@/lib/cases";
import { AppShell } from "@/components/app/AppShell";
import { Box, FIELD_STATUS } from "@/components/app/CaseWork";
import { Textarea } from "@/components/ui/textarea";

const Attempt = ({ v, reload }) => {
  const [outcome, setOutcome] = useState("confirmed");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [geo, setGeo] = useState({ lat: "", lng: "" });
  const [busy, setBusy] = useState(false);
  const locate = () => navigator.geolocation?.getCurrentPosition(
    (p) => setGeo({ lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }), () => toast.error("Location unavailable"));
  const send = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("outcome", outcome);
    fd.append("notes", notes);
    fd.append("lat", geo.lat);
    fd.append("lng", geo.lng);
    photos.forEach((p) => fd.append("photos", p));
    setBusy(true);
    try {
      await api.post(`/field/visits/${v.id}/attempt`, fd);
      toast.success("Visit recorded");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={send} className="mt-3 grid gap-3" data-testid={`field-form-${v.id}`}>
      <ul className="list-disc pl-5 text-xs text-oath-secondary">
        <li>Identify yourself truthfully. Never enter the home or look through windows.</li>
        <li>Photograph the exterior only, from the public right-of-way.</li>
        <li>Record what you saw, not what you assume.</li>
      </ul>
      <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm" data-testid={`field-outcome-${v.id}`}>
        <option value="confirmed">Occupancy confirmed</option><option value="not_confirmed">Occupancy not confirmed</option><option value="no_contact">No contact / inconclusive</option>
      </select>
      <Textarea rows={3} required placeholder="Factual notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`field-notes-${v.id}`} />
      <input type="file" accept="image/*" capture="environment" multiple onChange={(e) => setPhotos([...e.target.files].slice(0, 6))} className="text-sm" data-testid={`field-photos-${v.id}`} />
      <button type="button" onClick={locate} className="inline-flex w-fit items-center gap-1 text-xs text-oath-accent" data-testid={`field-locate-${v.id}`}><MapPin className="h-3.5 w-3.5" /> {geo.lat ? `${geo.lat}, ${geo.lng}` : "Add my location"}</button>
      <button disabled={busy} className="w-fit rounded-full bg-oath-ink px-5 py-2.5 font-mono-label text-[10px] font-semibold text-white disabled:opacity-50" data-testid={`field-submit-${v.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "SAVE ATTEMPT"}</button>
    </form>
  );
};

export default function FieldVisits() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/field/visits").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <AppShell eyebrow="— FIELD AGENT" title="My visits">
      <div className="grid gap-4" data-testid="field-visits">
        {rows === null && <Loader2 className="h-5 w-5 animate-spin" />}
        {rows?.length === 0 && <p className="text-sm text-oath-muted" data-testid="field-empty">No visits assigned to you.</p>}
        {rows?.map((v) => (
          <Box key={v.id} title={`${v.case_number} · ${v.field.city.toUpperCase()}`} testid={`field-visit-${v.id}`}>
            <p className="font-display text-lg">{fullAddress(v.address)}</p>
            <p className="text-sm text-oath-secondary">Confirm whether {v.name} lives here · {FIELD_STATUS[v.field.status]} · {v.field.attempts.length}/2 attempts</p>
            {["assigned", "attempted"].includes(v.field.status) && <Attempt v={v} reload={load} />}
          </Box>
        ))}
      </div>
    </AppShell>
  );
}
